import * as Sentry from '@sentry/react'
import { observer } from 'mobx-react'
import React, { useContext, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { DealerLayout } from '@/components/layout/DealerLayout'
import { NavBar } from '@/components/layout/NavBar'
import { PrivateRoute } from '@/components/routing/PrivateRoute'
import { PublicRoute } from '@/components/routing/PublicRoute'
import { AnnouncementBanner } from '@/components/shared/AnnouncementBanner'
import { AnnouncementDetailModal } from '@/components/shared/AnnouncementDetailModal'
import { ImpersonationBanner } from '@/components/shared/ImpersonationBanner'
import { SentryFeedback } from '@/components/shared/SentryFeedback'
import { TaskProgress } from '@/components/shared/TaskProgress'
import { Analytics } from '@/pages/Analytics'
import { Billing } from '@/pages/Billing'
import { BillingDetails } from '@/pages/BillingDetails'
import { Dashboard } from '@/pages/Dashboard'
import { DealerDetails } from '@/pages/DealerDetails'
import { Login } from '@/pages/Login'
import { MultiFactorAuthentication } from '@/pages/MultiFactorAuthentication'
import { NotFound } from '@/pages/NotFound'
import { PasswordReset } from '@/pages/PasswordReset'
import { PasswordResetRequest } from '@/pages/PasswordResetRequest'
import { PrivacyPolicy } from '@/pages/PrivacyPolicy'
import { RepairOrder } from '@/pages/RepairOrder'
import { RepairOrders } from '@/pages/RepairOrders'
import { RoIntake } from '@/pages/RoIntake'
import { Settings } from '@/pages/Settings'
import { TermsAndConditions } from '@/pages/TermsAndConditions'
import { UserProfile } from '@/pages/UserProfile'
import { Users } from '@/pages/Users'
import { BillingFilterStoreContext } from '@/stores/BillingFilterStore'
import { DealerFilterStoreContext } from '@/stores/DealerFilterStore'
import { GlobalSettingsContext } from '@/stores/GlobalSettingsStore'
import { RootStoreContext } from '@/stores/RootStore'
import { UserStoreContext } from '@/stores/UserStore'
import { clearDealerGroup } from '@/utils/analytics'

/**
 *
 * Routes
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */
const BillingRedirect = () => {
  const { dealerId } = useParams()
  return <Navigate to={`/dealer/${dealerId}/billing`} replace />
}

const PageRoutes = observer(() => {
  // Context
  const location = useLocation()
  const { clearStore } = useContext(RootStoreContext)
  const { isAuthenticated, isWpcUser, hasValidProfile } = useContext(UserStoreContext)
  const dealerFilterStore = useContext(DealerFilterStoreContext)
  const billingFilterStore = useContext(BillingFilterStoreContext)
  const { isGlobalRoListEnabled, isAnalyticsEnabled } = useContext(GlobalSettingsContext)

  useEffect(() => {
    // If the user is authenticated, but we don't have a valid profile stored we
    // need to reset local storage and force log them out.
    if (isAuthenticated && !hasValidProfile) {
      clearStore()
    }
  }, [isAuthenticated, hasValidProfile])

  // Attach the Replay integration only after authentication so the
  // DOM-wrapping surface it installs (rrweb) is absent from the
  // unauthenticated pages implicated in WARP-890. `SentryFeedback` adds the
  // Feedback integration itself, because its own effect runs before this one
  // (WARP-956).
  useEffect(() => {
    if (!isAuthenticated) return
    if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') return

    const client = Sentry.getClient()
    if (!client) return

    if (
      import.meta.env.VITE_SENTRY_REPLAYS_ENABLED === 'true' &&
      !client.getIntegrationByName('Replay')
    ) {
      Sentry.addIntegration(
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      )
    }
  }, [isAuthenticated])

  // Clear filter stores and analytics dealer group when navigating between
  // sections so events on unrelated screens don't inherit a previous
  // dealer's context.
  useEffect(() => {
    if (!isAuthenticated) return
    const path = location.pathname
    if (!path.startsWith('/billing') && !path.endsWith('/billing'))
      billingFilterStore.clearContext()
    if (!path.startsWith('/dealer/')) dealerFilterStore.clearContext()
    const isDealerScopedRoute = path.startsWith('/dealer/') || path.startsWith('/billing/')
    if (!isDealerScopedRoute) clearDealerGroup()
  }, [location.pathname])

  const options = useMemo(() => {
    const defaultOptions = [{ label: 'Dashboard', path: '/dashboard' }]

    // Global RO list is behind a backend feature flag.
    if (isGlobalRoListEnabled) {
      defaultOptions.push({ label: 'Repair Orders', path: '/repair-orders' })
    }

    // Analytics (Performance Explorer) is behind its own backend feature flag.
    if (isAnalyticsEnabled) {
      defaultOptions.push({ label: 'Analytics', path: '/analytics' })
    }

    if (isWpcUser) {
      defaultOptions.push({
        label: 'Users',
        path: '/users',
      })
    }
    if (isWpcUser) {
      defaultOptions.push({
        label: 'Billing',
        path: '/billing',
        type: 'wpc',
      })
    }

    return defaultOptions
  }, [isWpcUser, isGlobalRoListEnabled, isAnalyticsEnabled])

  return (
    <div className="bg-background h-screen w-screen">
      <Helmet titleTemplate="%s - WARP" defaultTitle="Warranty Processing Portal">
        <meta name="description" content="Warranty Processing Portal" />
      </Helmet>

      <NavBar options={options} orientation="horizontal" />

      {isAuthenticated && <ImpersonationBanner />}
      {isAuthenticated && <AnnouncementBanner />}
      {isAuthenticated && <AnnouncementDetailModal />}

      <div
        className={mergeClassNames(
          isAuthenticated
            ? 'bg-background h-[calc(100vh-64px)]'
            : 'bg-background min-h-[calc(100vh-200px)] md:min-h-[calc(100vh-200px)] lg:min-h-[calc(100vh-290px)]',
        )}
      >
        <Routes>
          {/* Main Routes */}
          <Route path="*" element={<NotFound />} />
          <Route element={<PrivacyPolicy />} caseSensitive path="/privacy-policy" />
          <Route element={<TermsAndConditions />} caseSensitive path="/terms-and-conditions" />

          {/* Public Routes */}
          <Route element={<PublicRoute component={Login} />} caseSensitive path="/" />
          <Route element={<PublicRoute component={Login} />} caseSensitive path="/login" />
          <Route
            element={<PublicRoute component={MultiFactorAuthentication} />}
            caseSensitive
            path="/verification"
          />
          <Route
            element={<PublicRoute component={PasswordReset} />}
            caseSensitive
            path="/password-reset"
          />
          <Route
            element={<PublicRoute component={PasswordReset} />}
            caseSensitive
            path="/set-password"
          />
          <Route
            element={<PublicRoute component={PasswordResetRequest} />}
            caseSensitive
            path="/password-reset-request"
          />

          <Route element={<PrivateRoute component={Dashboard} />} caseSensitive path="/" />

          {/* Private Routes */}
          <Route
            element={<PrivateRoute component={Dashboard} />}
            caseSensitive
            path="/dashboard"
          />

          <Route
            element={<PrivateRoute component={RepairOrders} />}
            caseSensitive
            path="/repair-orders"
          />

          <Route
            element={<PrivateRoute component={Analytics} />}
            caseSensitive
            path="/analytics"
          />

          <Route
            caseSensitive
            path="/profile"
            element={<PrivateRoute component={UserProfile} />}
          />
          <Route caseSensitive path="/settings" element={<PrivateRoute component={Settings} />} />

          <Route path="/dealer/:id" element={<PrivateRoute component={DealerLayout} />}>
            <Route path="detail" element={<DealerDetails />} />
            <Route path="intake" element={<RoIntake />} />
            <Route path="billing" element={<BillingDetails />} />
          </Route>
          <Route
            path="/dealer/:dealerId/repair-order/create"
            element={
              <PrivateRoute
                component={RepairOrder}
                // Mirrors the backend RepairOrder "add" perm (user_can_manage_ros): WPC
                // users or dealer admins. The self-managed-dealer gate lives on the "Add
                // Repair Order" button (canManageDraftRos), the only entry to this route;
                // RO Intake creates via its own modal, not here.
                restrictionCheck={(user) =>
                  user.userType === 'WPC User' || user.dealerUserProfile?.role === 'Administrator'
                }
                skipLayout
                skipFooter
              />
            }
          />
          <Route
            path="/dealer/:dealerId/repair-order/:id"
            element={<PrivateRoute component={RepairOrder} skipLayout skipFooter />}
          />
          <Route
            path="/users"
            element={
              <PrivateRoute
                component={Users}
                restrictionCheck={(user) => user.userType === 'WPC User'}
              />
            }
          />
          {/* WARP-939 renamed this page. Keep the old path working for bookmarks. */}
          <Route path="/dealer-users" element={<Navigate replace to="/users" />} />

          <>
            <Route
              path="/billing"
              element={
                <PrivateRoute
                  component={Billing}
                  restrictionCheck={(user) => user.userType === 'WPC User'}
                />
              }
            />
            <Route path="/billing/:dealerId/detail" element={<BillingRedirect />} />
          </>
        </Routes>
      </div>

      {isAuthenticated && import.meta.env.VITE_SENTRY_FEEDBACK_ENABLED === 'true' && (
        <SentryFeedback />
      )}
      <TaskProgress />
    </div>
  )
})

export default PageRoutes
